/**
 * Payload for the View Evidence modal (criteria copy, PDF paths, or text evidence).
 */

/** Context shown when reviewing atom evidence documents. */
export interface ViewEvidenceContext {
  /** Subtitle under the modal title (e.g. Criteria 7). */
  criteriaTitle: string;
  /** Rolled-up question / primary criterion copy. */
  primaryCriteria: string;
  /** Atom-level criterion copy. */
  subCriteria: string;
  /** Agent deciding fact for the patient. */
  patientFinding: string;
  /** Authenticated relative `/api/v1/...` paths, one per source tab. */
  sourcePaths: string[];
  /** Agent source label when no PDF path exists (e.g. FHIR field references). */
  textEvidenceSource?: string | null;
  /** Agent deciding fact shown beside {@link textEvidenceSource} when no PDF exists. */
  textEvidenceDecidingFact?: string | null;
}

/**
 * Collect unique non-empty eligibility source paths for tabs.
 *
 * @param paths - Candidate relative API paths
 * @returns Deduplicated paths in stable order
 */
export function collectEvidenceSourcePaths(
  paths: (string | null | undefined)[],
): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const path of paths) {
    const trimmed = path?.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    ordered.push(trimmed);
  }
  return ordered;
}

/**
 * Whether an atom carries non-PDF screening evidence (source and/or deciding fact).
 *
 * @param source - Agent source label (e.g. FHIR resource path)
 * @param decidingFact - Agent deciding fact for the verdict
 * @returns True when either field has non-empty text
 */
export function hasTextScreeningEvidence(
  source?: string | null,
  decidingFact?: string | null,
): boolean {
  return Boolean(source?.trim() || decidingFact?.trim());
}

/**
 * Whether View Evidence should be enabled for one atom.
 *
 * @param sourcePaths - Resolved PDF API paths for the atom
 * @param source - Agent source label when no PDF exists
 * @param decidingFact - Agent deciding fact when no PDF exists
 * @returns True when a PDF or text evidence can be shown
 */
export function canViewAtomEvidence(
  sourcePaths: string[],
  source?: string | null,
  decidingFact?: string | null,
): boolean {
  return sourcePaths.length > 0 || hasTextScreeningEvidence(source, decidingFact);
}
